/**
 * SPIKE: Preload edit page loadables from the view page for transition.
 */
import { EditPageLoaderServer } from '@confluence/full-page-editor/entry-points/EditPageLoaderServer.ts';
import { FullPageEditorLoader } from '@confluence/full-page-editor/entry-points/FullPageEditorLoader.ts';
import { NativeCollabLoader } from '@confluence/native-collab/entry-points/NativeCollabLoader.ts';
import { TitleAreaContentComponents } from '@confluence/title-area-content-components/entry-points/TitleAreaContentComponents.tsx';
import { EmojiTitleButtonLoader } from '@confluence/emoji-title/entry-points/EmojiTitleButton.tsx';
import { EmojiTitleSelection } from '@confluence/emoji-title/entry-points/EmojiTitleSelection.tsx';
import { EditorPageCoverPictureEntry } from '@confluence/page-cover-picture/entry-points/EditorPageCoverPictureEntry.ts';
import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button/entry-points/EditorAppearanceButtonNext.ts';
import { EditorContentModeButton } from '@confluence/editor-content-mode-button/entry-points/EditorContentModeButton.ts';
import { PublishButtonWithMoreOptions } from '@confluence/publish-modal/entry-points/PublishButtonWithMoreOptions.ts';
import { FavouriteButton } from '@confluence/favourite-button/entry-points/FavouriteButton.ts';
import {
	RestrictionsButtonLoaderPaint,
	RestrictionsButtonLoaderAfterPaint,
} from '@confluence/restrictions/entry-points/RestrictionsButton.ts';
import { LazyRestrictionsDialogMountPoint } from '@confluence/restrictions/entry-points/RestrictionsDialogTrigger.ts';
import { EditorBreadcrumbs } from '@confluence/editor-breadcrumbs/entry-points/EditorBreadcrumbs.ts';
import { BreadcrumbsLoader } from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import { SpaceNavigationLoader } from '@confluence/side-navigation/entry-points/SpaceNavigation.ts';
import { SpaceNavigationContent } from '@confluence/side-navigation/entry-points/SpaceNavigationContentLoaders.ts';
import { GlobalComponentsLoader } from '@confluence/global-components';
import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader.ts';
import { PageLayout } from '@confluence/page-layout/entry-points/PageLayout.ts';
import { BannerContainer } from '@confluence/banner-container';
import { CustomHeaderFooterLoader } from '@confluence/custom-header-footer';
import { PublishModalMountPoint } from '@confluence/publish-modal/entry-points/PublishModalMountPoint.ts';
import { PublishModalKeyboardShortcuts } from '@confluence/publish-modal/entry-points/PublishModalKeyboardShortcuts.ts';
import { ContentTools } from '@confluence/content-tools/entry-points/ContentTools.ts';
import { LazyForgeContentTools } from '@confluence/content-tools/entry-points/LazyForgeContentTools.ts';
import { AgentPresenceLoader } from '@confluence/team-presence/entry-points/AgentPresenceLoader.tsx';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { BlankPageQuickActions } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActions.ts';
import { WatchDialogWrapperLoader } from '@confluence/watch-dialog/entry-points/WatchDialogWrapper.tsx';
import { PageTreeLoader } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { EditPageLoadingListener } from '@confluence/load-edit-page/entry-points/EditPageLoadingListener.ts';
import { loadMainButtons } from '@confluence/editor-main-buttons';
import { PageTreeAfterIcon as PageTreeAfterIconLoader } from '@confluence/page-tree/entry-points/PageTreeAfterIcon.ts';
import { LazyWatchSettingsOverflowMenuShortcut } from '@confluence/watch-dialog/entry-points/WatchSettingsOverflowMenuShortcut.tsx';
import { ShortcutsSection } from '@confluence/space-shortcuts/entry-points/shortcutsSection.ts';
import { ReactionsLoader } from '@confluence/reactions/entry-points/ReactionsLoader.tsx';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState.ts';
import { EditorContentStateComponent as LazyEditorContentStateComponent } from '@confluence/content-state/entry-points/EditorContentStateComponent.ts';
import { PopupPreview } from '@confluence/template-preview/entry-points/PopupPreview.ts';
import { TemplatePreview } from '@confluence/template-preview/entry-points/TemplatePreview.ts';
import { ConversationAssistantNavBarButton } from '@confluence/conversation-assistant/entry-points/navBarButton.ts';
import { BlogTree } from '@confluence/blog-tree/entry-points/BlogTree.tsx';
import { EmptySidebarCollapsibleSectionLoader } from '@confluence/collapsible-section/entry-points/EmptySidebarCollapsibleSectionLoader.ts';
import { TreeMonitorLoader } from '@confluence/tree/entry-points/TreeMonitor.ts';
import { SpaceHoverPageCard as LazySpaceHoverPageCard } from '@confluence/page-card/entry-points/SpaceHoverPageCard.ts';
import { InvisibleLoadingContainer } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsLoadingContainer.ts';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter.ts';
import { EndOfPageRecommendationLoader } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationLoader.ts';
import { UnreadIndicator } from '@confluence/unread-comments/entry-points/UnreadIndicator.ts';
import { CommentEditor as LazyCommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { NavigationLockout } from '@confluence/navigation-lockout';
import { PresenterModeContextProvider } from '@confluence/presentation-mode';
import { SpacesDropdownLoader } from '@confluence/spaces-dropdown/entry-points/SpacesDropdown.ts';
import { GoToHomeLoader } from '@confluence/home-redirects/entry-points/GoToHome.ts';
import { LiveSearchLoader } from '@confluence/live-search/entry-points/liveSearchLoader.ts';
import { ADFRendererWithExtensions } from '@confluence/adf-renderer/entry-points/ADFRendererWithExtensions.ts';
import { ExcerptIncludeLoader } from '@confluence/excerpt-include/entry-points/loader.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { RestrictedPage } from '@confluence/no-permission/entry-points/RestrictedPage.ts';
import { ArchivedPageBanner } from '@confluence/archive-pages/entry-points/ArchivedPageBannerV2.ts';

const EDIT_PAGE_LOADABLES: Array<{ preload: () => void | Promise<unknown> }> = [
	EditPageLoaderServer,
	FullPageEditorLoader,
	NativeCollabLoader,
	TitleAreaContentComponents,
	EmojiTitleButtonLoader,
	EmojiTitleSelection,
	EditorPageCoverPictureEntry,
	EditorAppearanceButtonNext,
	EditorContentModeButton,
	PublishButtonWithMoreOptions,
	FavouriteButton,
	RestrictionsButtonLoaderPaint,
	RestrictionsButtonLoaderAfterPaint,
	LazyRestrictionsDialogMountPoint,
	EditorBreadcrumbs,
	BreadcrumbsLoader,
	SpaceNavigationLoader,
	SpaceNavigationContent,
	GlobalComponentsLoader,
	EditorContentState,
	InlineCommentsHighlighterLoader,
	PageLayout,
	BannerContainer,
	CustomHeaderFooterLoader,
	PublishModalMountPoint,
	PublishModalKeyboardShortcuts,
	ContentTools,
	LazyForgeContentTools,
	AgentPresenceLoader,
	GeneralShortcutListener,
	BlankPageQuickActions,
	WatchDialogWrapperLoader,
	PageTreeLoader,
	EditPageLoadingListener,
	PageTreeAfterIconLoader,
	LazyWatchSettingsOverflowMenuShortcut,
	ShortcutsSection,
	ReactionsLoader,
	RendererContentState,
	LazyEditorContentStateComponent,
	PopupPreview,
	TemplatePreview,
	ConversationAssistantNavBarButton,
	BlogTree,
	EmptySidebarCollapsibleSectionLoader,
	TreeMonitorLoader,
	LazySpaceHoverPageCard,
	InvisibleLoadingContainer,
	ReadingAidsAcronymsHighlighter,
	EndOfPageRecommendationLoader,
	UnreadIndicator,
	LazyCommentEditor,
	NavigationLockout,
	PresenterModeContextProvider,
	SpacesDropdownLoader,
	GoToHomeLoader,
	LiveSearchLoader,
	ADFRendererWithExtensions,
	ExcerptIncludeLoader,
	ObjectSidebarPanel,
	RestrictedPage,
	ArchivedPageBanner,
];

export const preloadEditPageLoadables = () => {
	const start = performance.now();

	const promises = [
		...EDIT_PAGE_LOADABLES.map((loadable) => loadable.preload()),
		loadMainButtons(),
	];

	void Promise.allSettled(promises).then((results) => {
		const duration = Math.round(performance.now() - start);
		const failed = results.filter((r) => r.status === 'rejected').length;

		performance.measure('preloadEditPageLoadables', {
			start,
			detail: { duration, succeeded: promises.length - failed, failed, total: promises.length },
		});
	});
};
